import { ApiFehlerSchema } from '@ksb/shared';
import type { z } from 'zod';

/** Fehler aus der API mit deutscher Meldung (`message`) und maschinenlesbarem `code`. */
export class ApiFehlerError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiFehlerError';
  }
}

type HeaderLieferant = () => Promise<Record<string, string>>;

let anmeldeHeader: HeaderLieferant = async () => ({});

/**
 * Die einzige Verbindung zwischen Anmeldung und API-Client (main.tsx): liefert je Aufruf
 * den `Authorization`-Header (MSAL) bzw. lokal die Header des Entwicklungs-Logins.
 */
export function setzeAnmeldeHeader(lieferant: HeaderLieferant): void {
  anmeldeHeader = lieferant;
}

/** Ruft einen API-Endpunkt auf und validiert die Antwort gegen das zod-Schema aus @ksb/shared. */
export async function apiAufruf<T extends z.ZodType>(
  pfad: string,
  schema: T,
  init?: RequestInit,
): Promise<z.infer<T>> {
  // Außerhalb des try: ein Anmeldefehler ist kein Netzwerkfehler und soll nicht so heißen.
  const header = await anmeldeHeader();
  let antwort: Response;
  try {
    antwort = await fetch(`/api/${pfad}`, {
      ...init,
      headers: { Accept: 'application/json', ...header, ...init?.headers },
    });
  } catch {
    throw new ApiFehlerError(
      0,
      'NETZWERK',
      'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Verbindung.',
    );
  }

  const body: unknown = await antwort.json().catch(() => undefined);

  if (!antwort.ok) {
    const fehler = ApiFehlerSchema.safeParse(body);
    if (fehler.success) {
      throw new ApiFehlerError(
        antwort.status,
        fehler.data.code,
        fehler.data.message,
        fehler.data.details,
      );
    }
    throw new ApiFehlerError(
      antwort.status,
      'UNBEKANNT',
      `Unerwartete Antwort vom Server (${antwort.status}).`,
    );
  }

  const ergebnis = schema.safeParse(body);
  if (!ergebnis.success) {
    throw new ApiFehlerError(
      antwort.status,
      'UNGUELTIGE_ANTWORT',
      'Die Antwort des Servers ist ungültig.',
    );
  }
  return ergebnis.data;
}

/** POST (oder PUT) mit JSON-Body. */
export function apiSenden<T extends z.ZodType>(
  pfad: string,
  schema: T,
  body: unknown,
  methode: 'POST' | 'PUT' = 'POST',
): Promise<z.infer<T>> {
  return apiAufruf(pfad, schema, {
    method: methode,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Für Fehlerbehandlung im UI: der Code eines API-Fehlers, sonst `undefined`. */
export function fehlerCode(fehler: unknown): string | undefined {
  return fehler instanceof ApiFehlerError ? fehler.code : undefined;
}

/** Deutsche Meldung für jeden Fehler, auch unerwartete. */
export function fehlerText(fehler: unknown): string {
  return fehler instanceof ApiFehlerError
    ? fehler.message
    : 'Es ist ein unerwarteter Fehler aufgetreten. Bitte laden Sie die Seite neu.';
}
